/**
 * Real AA_models region classification — ASPECTS/lobe/vascular "affected /
 * normal" calls with a probability, reproducing
 * `get_AA_report_list`/`_build_predict_df_from_models` in the real OpenADS
 * `src/ads/reporting/radiology/dwi_radiology_report.py`, and the L/R
 * combine arithmetic in `src/ads/reporting/features/qfv_builder.py`
 * (`vec_VascAtlas2visual`, `get_Vas_visual_prob_comb`, `vec_LobeAtlas2visual`
 * + its `lobe_dict = dict(zip(lobe_df['index'], range(len(lobe_df))))`,
 * `get_Aspect_visual_prob_comb`). Verified: the model's `vas_roi`/`lobe_roi`/
 * `aspect_roi_for_models` name lists and the real feature order match this
 * app's bundled atlas lookup region order exactly (confirmed by inspecting
 * `RF_*.pkl`'s `feature_cols` directly, not assumed) — see the per-atlas
 * combine functions below for the exact index arithmetic ported.
 *
 * SCOPE CUT (documented, not silent): only vascular (7 of 8 ROI models — the
 * real pipeline's 8th, "watershed", has no matching atlas territory bundled
 * in this app, see note below), lobe (11/11), and ASPECTS (10/10) are wired
 * — 28 of the real pipeline's ~34 AA_models. The other 6
 * (posterior-circulation ASPECTS "aspectpc": PCA/Thalamus/Cerebellum/Pons/
 * Midbrain, and "hydro" hydrocephalus) were originally unavailable. Hydro is
 * now wired as `computeHydrocephalus` (bottom of this file; needs the
 * ADC-driven VENTRICLES QFV, so `index.ts` only runs it when the report
 * input carries an MNI ADC). The aspectpc classifiers are still not
 * converted — reported as unavailable below.
 *
 * NOT implemented: SHAP TreeExplainer feature attribution. The real
 * pipeline's own inference path for this report table never calls SHAP —
 * only `.predict()`/`.predict_proba()` — so this module's "RF_predict"/
 * "RF_predict_prob" output is a faithful, complete port of what that real
 * codepath actually computes.
 */
import type { RegionStat } from "./atlas";
import { voxelVolumeMl } from "./volumes";
import type { Dims } from "../volume";
import { runWorker } from "../worker-client";
import type { AaPredictRequest, AaPredictResponse } from "../workers/aa-interpretation.worker";

import { appPath } from "../assets";
export interface RegionCall {
  region: string;
  predict: "AFFECTED" | "NORMAL" | "N.A.";
  probability: number | null;
  /** Lesion volume (mL) in this combined ROI, same number the real pipeline fed the model. */
  volumeMl: number;
}

export interface InterpretationResult {
  status: "ok" | "partial" | "error";
  vascular: RegionCall[];
  lobe: RegionCall[];
  aspects: RegionCall[];
  /** Real AA_models this pipeline does not have the atlas data to run yet. */
  unavailable: string[];
  error?: string;
}

/** `overlapVoxels` per region (same order as the bundled atlas lookup) -> mL. */
function regionMlByName(stats: RegionStat[], pixdim: Dims): Map<string, number> {
  const vv = voxelVolumeMl(pixdim);
  const m = new Map<string, number>();
  for (const s of stats) m.set(s.region, s.overlapVoxels * vv);
  return m;
}

/**
 * `vec_VascAtlas2visual` + `get_Vas_visual_prob_comb`, applied directly to
 * this app's own vascular atlas order (verified index-for-index identical
 * to the real function's expected input order: ACAL,ACAR,MLSL,MLSR,LLSL,
 * LLSR,MCAFL..MCAIR,PCATL..PCAOR,PCTPL..ACTPR,BL,BR,SCL,SCR,ICL,ICR,LVL,LVR).
 * Output: 8 bilateral-combined values in `vas_roi` order (ACA, MCA, PCA,
 * "cerebellar" [=SCL/R+ICL/R, the real pipeline's own naming, not relabeled
 * here], basilar, Lenticulostriate [=LLSL/R], "Choroidal & Thalamoperfurating"
 * [=PCTP+ACTP], watershed [=LVL/R]).
 */
export function vascularVisual8(byName: Map<string, number>): number[] {
  const v = (name: string) => byName.get(name) ?? 0;
  const aca = v("ACAL") + v("MLSL") + v("ACAR") + v("MLSR");
  const mca =
    v("MCAFL") + v("MCAPL") + v("MCATL") + v("MCAOL") + v("MCAIL") +
    v("MCAFR") + v("MCAPR") + v("MCATR") + v("MCAOR") + v("MCAIR");
  const pca = v("PCATL") + v("PCAOL") + v("PCATR") + v("PCAOR");
  const cerebellar = v("SCL") + v("ICL") + v("SCR") + v("ICR");
  const basilar = v("BL") + v("BR");
  const lenticulostriate = v("LLSL") + v("LLSR");
  const choroidalThalamoperf = v("PCTPL") + v("ACTPL") + v("PCTPR") + v("ACTPR");
  const watershed = v("LVL") + v("LVR");
  return [aca, mca, pca, cerebellar, basilar, lenticulostriate, choroidalThalamoperf, watershed];
}

/**
 * `vec_LobeAtlas2visual` (via `lobe_dict` = name->index in this app's own
 * lobe atlas order, confirmed identical construction) then
 * `get_Lobe_visual_prob_comb`. Output: 11 values in `lobe_roi` order.
 */
export function lobeVisual11(byName: Map<string, number>): number[] {
  const v = (name: string) => byName.get(name) ?? 0;
  return [
    v("BasalGanglia_L") + v("BasalGanglia_R"),
    v("CSO_L") + v("CorRad_L") + v("CSO_R") + v("CorRad_R"),
    v("cerebellum_L") + v("cerebellum_R"),
    v("frontal_L") + v("frontal_R"),
    v("insula_L") + v("insula_R"),
    v("IntCapsule_L") + v("IntCapsule_R"),
    // get_Lobe_visual_prob_comb: `out[6] = lobe_prob[12] * 2` -- brainstem has
    // no L/R split in vec_LobeAtlas2visual (visual_vec[12] is already a
    // single combined midbrain+pons+medulla value), but the real pairwise
    // combine still doubles it to match the L+R-summed scale of every other
    // feature. Verified against the real function numerically, not assumed
    // from reading the code -- an earlier pass without the doubling was off
    // by exactly 2x on this one feature.
    (v("midbrain") + v("pons") + v("medulla")) * 2,
    v("occipital_L") + v("occipital_R"),
    v("parietal_L") + v("parietal_R"),
    v("temporal_L") + v("temporal_R"),
    v("Thalamus_L") + v("Thalamus_R"),
  ];
}

/**
 * `get_Aspect_visual_prob_comb`: pairwise L+R sum over this app's own
 * aspects atlas order (verified identical: caudate,lentiform,intern_capsule,
 * insula,M1,M2,M3,M6,M5,M4, each _L/_R pair, then "brain" dropped — the real
 * function sums indices [0:20:2]+[1:20:2], excluding index 20 "brain").
 * Output: 10 values in `aspect_roi_for_models` order.
 */
export function aspectsVisual10(byName: Map<string, number>): number[] {
  const v = (name: string) => byName.get(name) ?? 0;
  const pairs: [string, string][] = [
    ["caudate_L", "caudate_R"],
    ["lentiform_L", "lentiform_R"],
    ["intern_capsule_L", "intern_capsule_R"],
    ["insula_L", "insula_R"],
    ["M1_L", "M1_R"],
    ["M2_L", "M2_R"],
    ["M3_L", "M3_R"],
    ["M6_L", "M6_R"],
    ["M5_L", "M5_R"],
    ["M4_L", "M4_R"],
  ];
  return pairs.map(([l, r]) => v(l) + v(r));
}

// The real pipeline's own `vas_roi` list has 8 names, but only 7 have a
// matching `.pkl` under `assets/models/AA_models/vascular/` — "watershed" has
// no model in the real pipeline either (`_build_predict_df_from_models`'s own
// `if not cand: ... "N.A."` fallback), confirmed by listing the real source
// tree, not assumed. Reproduced faithfully: 7 real models, not 8.
const VAS_MODELS = ["ACA", "MCA", "PCA", "cerebellar", "basilar", "Lenticulostriate", "Choroidal_Thalamoperfurating"];
const LOBE_MODELS = [
  "basal_ganglia", "deep_white_matter", "cerebellum", "frontal", "insula",
  "internal_capsule", "brainstem", "occipital", "parietal", "temporal", "thalamus",
];
const ASPECT_MODELS = ["Caudate", "lentiform", "IC", "insula", "M1", "M2", "M3", "M6", "M5", "M4"];

function buildRequests(
  totalMl: number, roiNames: string[], values: number[], urlPrefix: string, fileNames: string[]
): AaPredictRequest[] {
  return roiNames.map((_, i) => ({
    modelUrl: `${urlPrefix}/${fileNames[i]}.onnx`,
    features: [totalMl, ...values],
  }));
}

async function runBatch(requests: AaPredictRequest[]): Promise<AaPredictResponse[]> {
  const worker = new Worker(appPath("workers/aa-interpretation.worker.js"), { type: "module" });
  try {
    const { results } = await runWorker<{ requests: AaPredictRequest[] }, { results: AaPredictResponse[] }>(
      worker,
      { requests },
      () => {},
    );
    return results;
  } finally {
    worker.terminate();
  }
}

function toCalls(names: string[], values: number[], results: AaPredictResponse[]): RegionCall[] {
  return names.map((region, i) => ({
    region,
    predict: results[i].label === 1 ? "AFFECTED" : "NORMAL",
    probability: Math.round(results[i].probability * 1000) / 1000,
    volumeMl: Math.round(values[i] * 1000) / 1000,
  }));
}

export async function computeInterpretation(
  atlasStats: Record<string, RegionStat[]>,
  totalLesionMl: number,
  pixdim: Dims,
): Promise<InterpretationResult> {
  try {
    const vasByName = regionMlByName(atlasStats.vascular ?? [], pixdim);
    const lobeByName = regionMlByName(atlasStats.lobe ?? [], pixdim);
    const aspByName = regionMlByName(atlasStats.aspects ?? [], pixdim);

    const vasValues = vascularVisual8(vasByName);
    const lobeValues = lobeVisual11(lobeByName);
    const aspValues = aspectsVisual10(aspByName);

    const vasReq = buildRequests(totalLesionMl, VAS_MODELS, vasValues, "/models/aa/vascular", VAS_MODELS);
    const lobeReq = buildRequests(totalLesionMl, LOBE_MODELS, lobeValues, "/models/aa/lobe", LOBE_MODELS);
    const aspReq = buildRequests(totalLesionMl, ASPECT_MODELS, aspValues, "/models/aa/aspects", ASPECT_MODELS);

    const [vasRes, lobeRes, aspRes] = await Promise.all([
      runBatch(vasReq), runBatch(lobeReq), runBatch(aspReq),
    ]);

    return {
      status: "ok",
      vascular: toCalls(VAS_MODELS, vasValues, vasRes),
      lobe: toCalls(LOBE_MODELS, lobeValues, lobeRes),
      aspects: toCalls(ASPECT_MODELS, aspValues, aspRes),
      unavailable: [
        "Posterior-circulation ASPECTS (PCA/Thalamus/Cerebellum/Pons/Midbrain) region calls — the ASPECTSPC atlas and QFV CSV are wired, but its AA_models/aspectpc classifiers are not converted to ONNX.",
        "Vascular — watershed — the real pipeline has no trained model for this ROI either (reports N.A.).",
      ],
    };
  } catch (err) {
    return {
      status: "error",
      vascular: [], lobe: [], aspects: [],
      unavailable: [],
      error: (err as Error).message,
    };
  }
}

/** `get_AA_report_list`'s hydro branch: one call for the whole brain, no QFV scalar. */
export interface HydroCall {
  predict: "AFFECTED" | "NORMAL";
  probability: number;
}

/**
 * Real `AA_models/hydro/RF_hydro_hydrocephalus.pkl`
 * (`Pipeline(StandardScaler, RandomForestClassifier(300))`), converted to
 * `/models/aa/hydro/hydrocephalus.onnx` with skl2onnx (zipmap off; checked
 * against sklearn on the reference subject — P(hydro) 0.0233333 both — and
 * 2000 random vectors, max |dP| 2.2e-7). Its `feature_cols` are
 * `[volml, LVOR1..10, LVIR1..10]`; native (`dwi_radiology_report.py`)
 * reads them back from `VASCULAR_QFV.csv`'s volml and `VENTRICLES_QFV.csv`,
 * i.e. the same rounded values `qfv.ts` writes — pass those in.
 */
export async function computeHydrocephalus(volml: number, ventricleQfv20: number[]): Promise<HydroCall> {
  if (ventricleQfv20.length !== 20) throw new Error(`hydro: expected 20 ventricle QFV values, got ${ventricleQfv20.length}`);
  const [res] = await runBatch([{ modelUrl: "/models/aa/hydro/hydrocephalus.onnx", features: [volml, ...ventricleQfv20] }]);
  return {
    predict: res.label === 1 ? "AFFECTED" : "NORMAL",
    probability: Math.round(res.probability * 1000) / 1000,
  };
}
